import { ReactComponent as EmptyPanelDroneIcon } from '@assets/components/panel/notification/empty-panel-drone-icon.svg';
import { IcAlert, IcProcedure } from '@assets/icons/24x24';
import { Box, Center, HStack, Icon, Spinner, Tabs, Text, VStack } from '@chakra-ui/react';
import { NotificationPlatformList } from '@components/cockpit/panels/notification/card/NotificationPlatformList';
import TargetId from '@components/map/popup/infos/targets/TargetId';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectNotificationById } from '@redux/data/data.selectors';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetDetailQuery } from '@services/data/notification.api';
import { getNotificationName } from '@utils/data/notification.utils';
import { FormattedMessage } from 'react-intl';

import { getNotificationIcon } from '../card/NotificationCardName';
import AlertsDetail from './platforms/AlertsDetail';
import ProcedureTab from './procedure/ProcedureTab';

type Props = {
  selectedNotification: string | null;
  isArchived?: boolean;
  isSearch?: boolean;
};

export default function NotificationDetails({
  selectedNotification,
  isArchived = false,
  isSearch = false,
}: Readonly<Props>) {
  const { data: notificationDetails, isFetching } = useGetDetailQuery(
    selectedNotification === null
      ? skipToken
      : {
          id: selectedNotification,
          withArchivedAlerts: isArchived,
          withDoneProcedures: isSearch,
        },
    { refetchOnMountOrArgChange: true },
  );
  const liveNotification = useAppSelector((state) => selectNotificationById(state, selectedNotification ?? ''));

  if (notificationDetails === undefined || selectedNotification !== notificationDetails.notification.id) {
    const isNewNotificationFetching =
      notificationDetails !== undefined && selectedNotification !== notificationDetails.notification.id && isFetching;

    return (
      <Box
        width="464px"
        height="100%"
        flexShrink={0}
        backgroundColor={isNewNotificationFetching ? 'neutral.700' : 'neutral.black'}
      >
        <Center width="100%" height="100%">
          {isFetching || isNewNotificationFetching ? (
            <Spinner color="neutral.200" boxSize="32px" />
          ) : (
            <VStack gap={2} paddingTop="58px">
              <Icon width="194px" height="194px" as={EmptyPanelDroneIcon} />
              <Text
                textStyle="lg"
                color="neutral.200"
                maxWidth="230px"
                fontFamily="space"
                fontFeatureSettings="'liga' off"
                textAlign="center"
              >
                <FormattedMessage
                  id={`components.notification.detail.${selectedNotification === null ? 'empty' : 'notFound'}`}
                />
              </Text>
            </VStack>
          )}
        </Center>
      </Box>
    );
  }

  const { notification, currentProcedure, doneProcedures } = notificationDetails;

  const notificationName = getNotificationName(liveNotification ?? notification);
  const notificationIdentifier = liveNotification?.identifier ?? notification.identifier;

  return (
    <VStack
      width="464px"
      height="100%"
      flexShrink={0}
      backgroundColor={selectedNotification ? 'neutral.900' : 'neutral.black'}
    >
      <VStack gap={0.25} height="100%" width="100%" backgroundColor="neutral.900">
        <HStack gap={2} height="104px" flexShrink={0} width="100%" backgroundColor="neutral.700" paddingLeft="40px">
          {getNotificationIcon(liveNotification ?? notification, { isArchived })}
          <VStack gap={0.5} alignItems="start" paddingRight={3}>
            <Tooltip content={notificationName ?? notificationIdentifier}>
              {notificationName ? (
                <Text
                  paddingX={0.5}
                  lineClamp={1}
                  maxWidth="100%"
                  cursor="default"
                  backgroundColor="neutral.black"
                  textTransform="uppercase"
                  lineHeight="18px"
                  wordBreak="break-all"
                >
                  {notificationName}
                </Text>
              ) : (
                <TargetId label={notificationIdentifier} isTechnical />
              )}
            </Tooltip>
            <NotificationPlatformList marks={liveNotification?.marks ?? notification.marks} isNotificationSelected />
          </VStack>
        </HStack>
        {(!isSearch && currentProcedure === null) || (isSearch && doneProcedures.length === 0) ? (
          <AlertsDetail notificationDetails={notificationDetails} isFetching={isFetching} />
        ) : (
          <Tabs.Root variant="neutral" gap={0.25} defaultValue="procedure" lazyMount>
            <Tabs.List>
              <Tabs.Trigger value="procedure">
                <Icon as={IcProcedure} boxSize="24px" color="inherit" />
              </Tabs.Trigger>
              <Tabs.Trigger value="alerts">
                <Icon as={IcAlert} boxSize="24px" color="inherit" />
              </Tabs.Trigger>
            </Tabs.List>
            <Tabs.ContentGroup>
              <ProcedureTab notificationDetails={notificationDetails} isSearch={isSearch} isFetching={isFetching} />
              <Tabs.Content value="alerts">
                <AlertsDetail notificationDetails={notificationDetails} isFetching={isFetching} />
              </Tabs.Content>
            </Tabs.ContentGroup>
          </Tabs.Root>
        )}
      </VStack>
    </VStack>
  );
}
